import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { TrendingUp, MapPin, Target } from "lucide-react";

const GrowthAddOns = () => {
  return (
    <>
      <SEO 
        title="Growth Add-Ons for Lawn Care & Landscaping | Get More Clients"
        description="Scale your lawn care or landscaping business with Google Ads and local SEO. Proven growth services that get you more clients."
        keywords="lawn care marketing, google ads lawn care, local SEO landscaping, landscaping marketing"
        canonical="https://www.leafrushmarketing.com/growth-add-ons/"
        ogUrl="https://www.leafrushmarketing.com/growth-add-ons/"
      />
      <div className="flex flex-col">
      {/* Hero */}
      <section className="py-20 md:py-32">
        <div className="container px-4 md:px-6">
          <div className="mx-auto max-w-4xl text-center space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Scale Your <span className="text-primary">Lawn Care Website</span>: Growth Add-Ons
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              After your lawn care website design or landscaping website design is live, amplify your reach with proven growth services designed specifically for your industry.
            </p>
          </div>
        </div>
      </section>

      {/* Services Cards */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Google Ads Card */}
            <Card className="border-2 hover:border-primary transition-all hover:shadow-lg">
              <CardHeader className="space-y-4">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Google Ads for Lawn Care & Landscaping</CardTitle>
                <CardDescription className="text-base">
                  Targeted ads that reach homeowners actively searching for your services. We build conversion-optimized funnels that turn clicks into customers.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Hyper-targeted ads for lawn care and landscaping searches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Conversion-optimized landing pages that close leads</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Complete call and lead tracking to measure ROI</span>
                  </li>
                </ul>

                <div className="pt-4 space-y-3">
                  <NavLink to="/growth-add-ons/google-ads">
                    <Button size="lg" className="w-full">Learn More About Google Ads</Button>
                  </NavLink>
                  <NavLink to="/contact">
                    <Button size="lg" variant="outline" className="w-full">Book Strategy Call</Button>
                  </NavLink>
                </div>
              </CardContent>
            </Card>

            {/* GBP Card */}
            <Card className="border-2 hover:border-primary transition-all hover:shadow-lg">
              <CardHeader className="space-y-4">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MapPin className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Google Business Profile Optimization</CardTitle>
                <CardDescription className="text-base">
                  Dominate the local map pack and get more calls without spending on ads. Monthly optimization to keep you visible when homeowners search locally.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Review generation and management to build trust</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Map pack ranking optimization for local searches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>Monthly Google Posts to maintain visibility</span>
                  </li>
                </ul>

                <div className="pt-4 space-y-3">
                  <NavLink to="/growth-add-ons/gbp">
                    <Button size="lg" className="w-full">Learn More About GBP</Button>
                  </NavLink>
                  <NavLink to="/contact">
                    <Button size="lg" variant="outline" className="w-full">Book Strategy Call</Button>
                  </NavLink>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Your Website Design Is the Foundation */}
      <section className="py-20 bg-primary/5">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Website Design Is the Foundation</h2>
              <p className="text-xl text-muted-foreground">
                Every add-on service we offer builds on your lawn care website design or landscaping website design. We don't run ads without a high-converting landing page. We don't optimize GBP without aligning it to your website's service areas.
              </p>
            </div>
            <p className="text-muted-foreground text-center mb-6">
              That's why we recommend starting with a professional lawn care web design or landscaping web design before layering on growth services. Your website is the conversion layer that turns traffic into paying customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/websites/">
                <Button variant="outline">Website Design Pricing</Button>
              </NavLink>
              <NavLink to="/lawn-care-website-design/">
                <Button variant="outline">Lawn Care Website Design</Button>
              </NavLink>
              <NavLink to="/landscaping-website-design/">
                <Button variant="outline">Landscaping Website Design</Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Why Both Services */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Best Results with Both Services</h2>
              <p className="text-xl text-muted-foreground">
                Google Ads provides immediate leads. GBP optimization builds long-term organic visibility. Together, they create a complete growth engine.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 text-center">
              <Card>
                <CardContent className="pt-6">
                  <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Immediate Impact</h3>
                  <p className="text-sm text-muted-foreground">
                    Google Ads brings qualified leads from day one
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Long-Term Visibility</h3>
                  <p className="text-sm text-muted-foreground">
                    GBP optimization builds sustainable organic traffic
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Target className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Complete Coverage</h3>
                  <p className="text-sm text-muted-foreground">
                    Capture customers at every stage of their search journey
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold">Ready to Scale Your Business?</h2>
            <p className="text-xl text-muted-foreground">
              Let's discuss which growth services make sense for your current stage and goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/contact#get-started">
                <Button size="lg">Get Started</Button>
              </NavLink>
              <NavLink to="/websites">
                <Button size="lg" variant="outline">View Website Packages</Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default GrowthAddOns;
